import React, { useState, useMemo, useEffect, useRef } from 'react';
import { Table, Button, Space, Tag, Switch, Divider, Select, message, Tooltip, Card } from 'antd';
import { DownloadOutlined, EyeOutlined, EyeInvisibleOutlined } from '@ant-design/icons';
import { getDataForms, PHD_TYPES, getSubYear, getFieldValue } from '../utils/dataForms';
import { getApiBaseURL } from '../utils/api';
import { getToken } from '../utils/auth';

/**
 * PhD authorization data export preview matrix
 *
 * All indicator types are merged into one master table, with indicator type columns merged via rowSpan,
 * rows=task items, columns=years, supports toggling display of target values and achievement rates.
 */

// ─── Pure functions ─────────────────────────────────────────────────

function getCellValueFromSubmission(sub, fieldKey) {
  if (sub.cells && sub.cells.length > 0) {
    const cell = [...sub.cells].reverse().find(c => c.field_key === fieldKey);
    if (cell && cell.value !== null && cell.value !== undefined && cell.value !== '') {
      const n = Number(cell.value);
      return isNaN(n) ? cell.value : n;
    }
    return null;
  }
  const v = getFieldValue(sub.data_content, fieldKey);
  return v !== undefined ? v : null;
}

function latestByYear(submissions, dataType) {
  const typeSubs = (submissions || []).filter(s => s.data_type === dataType && s.status === 'approved');
  const sorted = [...typeSubs].sort((a, b) => {
    const ta = new Date(a.reviewed_at || a.submitted_at || 0).getTime();
    const tb = new Date(b.reviewed_at || b.submitted_at || 0).getTime();
    return ta - tb;
  });
  const byYear = new Map();
  sorted.forEach(s => {
    const y = getSubYear(s);
    if (y == null) return;
    const cur = byYear.get(y);
    if (!cur) {
      byYear.set(y, { ...s, cells: [...(s.cells || [])] });
      return;
    }
    const mergedCells = [...(cur.cells || [])];
    (s.cells || []).forEach(c => {
      const idx = mergedCells.findIndex(mc => mc.field_key === c.field_key);
      if (idx >= 0) mergedCells[idx] = c;
      else mergedCells.push(c);
    });
    byYear.set(y, { ...cur, cells: mergedCells });
  });
  return byYear;
}

function findTarget(targetRecords, dataType, fieldKey, year) {
  if (!targetRecords?.length) return null;
  const range = targetRecords.find(
    t => t.data_type === dataType && t.field_key === fieldKey
      && t.start_year && t.end_year && year >= t.start_year && year <= t.end_year
  );
  if (range) return range.target_value;
  const exact = targetRecords.find(
    t => t.data_type === dataType && t.field_key === fieldKey && t.target_year === year
  );
  if (exact) return exact.target_value;
  const total = targetRecords.find(
    t => t.data_type === dataType && t.field_key === fieldKey
      && !t.start_year && !t.end_year
  );
  if (total) return total.target_value;
  return null;
}

function findTotalTarget(targetRecords, dataType, fieldKey) {
  if (!targetRecords?.length) return null;
  const total = targetRecords.find(
    t => t.data_type === dataType && t.field_key === fieldKey
      && (!t.start_year || t.start_year === null)
      && (!t.end_year || t.end_year === null)
  );
  return total ? total.target_value : null;
}

function calcRate(value, target) {
  if (target == null || target === 0) return null;
  if (value == null) return 0;
  return (value / target) * 100;
}

function getRateDisplay(rate) {
  if (rate == null) return { text: '—', color: '#999' };
  const fmt = `${rate.toFixed(1)}%`;
  if (rate < 100) return { text: fmt, color: '#faad14' };
  if (rate === 100) return { text: fmt, color: '#52c41a' };
  return { text: fmt, color: '#1677ff' };
}

// ─── Component ──────────────────────────────────────────────────────

const PhdExportMatrixPreview = ({ dataForms, submissions, departmentId, targetRecords }) => {
  const [showTargets, setShowTargets] = useState(false);

  const phdForms = useMemo(
    () => (dataForms || []).filter(f => PHD_TYPES.includes(f.key)),
    [dataForms]
  );

  const allYears = useMemo(() => {
    const set = new Set();
    (submissions || []).forEach(sub => {
      const y = getSubYear(sub);
      if (y != null) set.add(y);
    });
    return [...set].sort((a, b) => a - b);
  }, [submissions]);

  const initialRangeSet = useRef(false);
  const [yearStart, setYearStart] = useState(() => {
    if (allYears.length > 0) return Math.min(...allYears);
    return new Date().getFullYear() - 2;
  });
  const [yearEnd, setYearEnd] = useState(() => {
    if (allYears.length > 0) return Math.max(...allYears);
    return new Date().getFullYear() + 3;
  });
  useEffect(() => {
    if (allYears.length === 0 || initialRangeSet.current) return;
    initialRangeSet.current = true;
    setYearStart(Math.min(...allYears));
    setYearEnd(Math.max(...allYears));
  }, [allYears]);

  const yearOptions = allYears.length > 0
    ? allYears
    : Array.from({ length: 6 }, (_, i) => new Date().getFullYear() - 2 + i);

  const displayYears = [];
  for (let y = yearStart; y <= yearEnd; y++) displayYears.push(y);

  // For each indicator type: one merged submission per year
  const typeYearMap = useMemo(() => {
    const map = {};
    PHD_TYPES.forEach(dt => {
      map[dt] = latestByYear(submissions, dt);
    });
    return map;
  }, [submissions]);

  const canDownloadTotal = useMemo(() => {
    return PHD_TYPES.some(dt => (typeYearMap[dt]?.size || 0) > 0);
  }, [typeYearMap]);

  const handleDownloadTotal = async () => {
    try {
      const token = getToken();
      if (!token) { message.error('未登录'); return; }
      const apiUrl = getApiBaseURL();
      const resp = await fetch(
        `${apiUrl}/submissions/export-phd-total?department_id=${departmentId}&include_targets=${showTargets}&year_start=${yearStart}&year_end=${yearEnd}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!resp.ok) throw new Error('下载失败');
      const blob = await resp.blob();
      const disposition = resp.headers.get('Content-Disposition') || '';
      const match = disposition.match(/filename\*?=(?:UTF-8'')?([^;\s]+)/);
      const filename = match ? decodeURIComponent(match[1]) : `博士授权点总表_${Date.now()}.xlsx`;
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (e) {
      message.error(e.message || '下载失败');
    }
  };

  // ── Merge rows across all types ──

  const allRows = useMemo(() => {
    const result = [];
    phdForms.forEach(form => {
      const fields = (form.fields || []).filter(f => f.key !== '年份' && f.key !== 'year');
      fields.forEach(field => {
        result.push({
          key: `${form.key}::${field.key}`,
          dataType: form.key,
          indicatorLabel: form.label,
          indicatorRowSpan: 0,
          fieldKey: field.key,
          taskLabel: field.label,
        });
      });
    });
    // rowSpan to merge rows of the same indicator
    for (let i = 0; i < result.length;) {
      const label = result[i].indicatorLabel;
      let j = i + 1;
      while (j < result.length && result[j].indicatorLabel === label) j++;
      result[i].indicatorRowSpan = j - i;
      for (let k = i + 1; k < j; k++) result[k].indicatorRowSpan = 0;
      i = j;
    }
    return result;
  }, [phdForms]);

  // ── Build columns (all types share the same set of columns) ──

  const columns = useMemo(() => {
    const cols = [
      {
        title: '指标类型',
        key: 'indicator',
        width: 100,
        fixed: 'left',
        render: (_, r) => {
          if (r.indicatorRowSpan === 0) {
            return { children: null, props: { rowSpan: 0 } };
          }
          return {
            children: <span style={{ fontWeight: 600 }}>{r.indicatorLabel}</span>,
            props: { rowSpan: r.indicatorRowSpan || 1 },
          };
        },
      },
      {
        title: '任务项',
        dataIndex: 'taskLabel',
        key: 'task',
        width: 260,
        fixed: 'left',
        render: (text) => (
          <span style={{ paddingLeft: text.startsWith('其中:') ? 20 : 0, fontSize: 13 }}>
            {text}
          </span>
        ),
      },
    ];

    if (showTargets) {
      displayYears.forEach(y => {
        cols.push({
          title: `${y}年`,
          key: `y-${y}`,
          width: 270,
          align: 'center',
          children: [
            {
              title: '数据值',
              key: `y-${y}-val`,
              width: 90,
              align: 'center',
              render: (_, row) => {
                const typeYears = typeYearMap[row.dataType];
                const sub = typeYears?.get(y);
                if (!sub) return '—';
                const v = getCellValueFromSubmission(sub, row.fieldKey);
                return v != null ? <span style={{ fontWeight: 600, fontSize: 15 }}>{v}</span> : '—';
              },
            },
            {
              title: '目标值',
              key: `y-${y}-tgt`,
              width: 90,
              align: 'center',
              render: (_, row) => {
                const t = findTarget(targetRecords, row.dataType, row.fieldKey, y);
                return t != null ? <span style={{ color: '#1677ff' }}>{t}</span> : '—';
              },
            },
            {
              title: '达成率',
              key: `y-${y}-rate`,
              width: 90,
              align: 'center',
              render: (_, row) => {
                const typeYears = typeYearMap[row.dataType];
                const sub = typeYears?.get(y);
                const val = sub ? getCellValueFromSubmission(sub, row.fieldKey) : null;
                const tgt = findTarget(targetRecords, row.dataType, row.fieldKey, y);
                const rate = calcRate(val, tgt);
                const { text, color } = getRateDisplay(rate);
                return <span style={{ color, fontWeight: 600 }}>{text}</span>;
              },
            },
          ],
        });
      });

      // Total target column
      cols.push({
        title: '总目标',
        key: 'total_target_group',
        align: 'center',
        children: [
          {
            title: '数据值',
            key: 'total_target_val',
            width: 90,
            align: 'center',
            render: (_, row) => {
              const typeYears = typeYearMap[row.dataType];
              if (!typeYears) return '—';
              let latestVal = null, latestY = -Infinity;
              for (const [y, sub] of typeYears.entries()) {
                if (!sub) continue;
                const v = getCellValueFromSubmission(sub, row.fieldKey);
                if (v != null && y > latestY) { latestVal = v; latestY = y; }
              }
              return latestVal != null ? <span style={{ fontWeight: 600, fontSize: 15 }}>{latestVal}</span> : '—';
            },
          },
          {
            title: '目标值',
            key: 'total_target_tgt',
            width: 90,
            align: 'center',
            render: (_, row) => {
              const t = findTotalTarget(targetRecords, row.dataType, row.fieldKey);
              return t != null ? <span style={{ color: '#722ed1', fontWeight: 600 }}>{t}</span> : '—';
            },
          },
          {
            title: '达成率',
            key: 'total_target_rate',
            width: 90,
            align: 'center',
            render: (_, row) => {
              const typeYears = typeYearMap[row.dataType];
              if (!typeYears) return '—';
              let latestVal = null, latestY = -Infinity;
              for (const [y, sub] of typeYears.entries()) {
                if (!sub) continue;
                const v = getCellValueFromSubmission(sub, row.fieldKey);
                if (v != null && y > latestY) { latestVal = v; latestY = y; }
              }
              const tgt = findTotalTarget(targetRecords, row.dataType, row.fieldKey);
              const rate = calcRate(latestVal, tgt);
              const { text, color } = getRateDisplay(rate);
              return <span style={{ color, fontWeight: 600 }}>{text}</span>;
            },
          },
        ],
      });
    } else {
      displayYears.forEach(y => {
        cols.push({
          title: `${y}年`,
          key: `y-${y}`,
          width: 100,
          align: 'center',
          render: (_, row) => {
            const typeYears = typeYearMap[row.dataType];
            const sub = typeYears?.get(y);
            if (!sub) return '—';
            const v = getCellValueFromSubmission(sub, row.fieldKey);
            return v != null ? <span style={{ fontWeight: 600, fontSize: 15 }}>{v}</span> : '—';
          },
        });
      });
    }

    return cols;
  }, [displayYears, typeYearMap, targetRecords, showTargets]);

  const hasAnyData = PHD_TYPES.some(dt => (typeYearMap[dt]?.size || 0) > 0);

  if (!phdForms.length) return null;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 8 }}>
        <Space wrap>
          <span style={{ fontWeight: 'bold' }}>年份范围：</span>
          <Select value={yearStart} onChange={setYearStart} style={{ width: 100 }}>
            {yearOptions.map(y => (
              <Select.Option key={y} value={y}>{y}</Select.Option>
            ))}
          </Select>
          <span>—</span>
          <Select value={yearEnd} onChange={setYearEnd} style={{ width: 100 }}>
            {yearOptions.map(y => (
              <Select.Option key={y} value={y}>{y}</Select.Option>
            ))}
          </Select>
          <Divider type="vertical" />
          <span style={{ color: '#666' }}>显示目标</span>
          <Switch
            checked={showTargets}
            onChange={setShowTargets}
            checkedChildren={<EyeOutlined />}
            unCheckedChildren={<EyeInvisibleOutlined />}
          />
        </Space>
        <Tooltip title={canDownloadTotal ? '下载包含所有指标类型和年份的汇总 Excel' : '暂无已审核数据可下载'}>
          <Button
            type="primary"
            icon={<DownloadOutlined />}
            onClick={handleDownloadTotal}
            disabled={!canDownloadTotal}
          >
            下载总表
          </Button>
        </Tooltip>
      </div>

      {!hasAnyData ? (
        <div style={{ textAlign: 'center', padding: 60, color: '#999' }}>
          <div style={{ fontSize: 48, marginBottom: 16 }}>📊</div>
          <p style={{ fontSize: 16 }}>暂无数据可预览</p>
        </div>
      ) : (
        <Table
          columns={columns}
          dataSource={allRows}
          rowKey="key"
          pagination={false}
          size="small"
          bordered
          scroll={{ x: Math.max(800, 360 + displayYears.length * (showTargets ? 270 : 100) + (showTargets ? 270 : 0)) }}
        />
      )}
    </div>
  );
};

export default PhdExportMatrixPreview;
